import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import {
  Filter,
  FilterField,
  FilterForm,
  FilterValueType,
  FilterVariable,
} from '../filtered-search.component';
import { isInteger } from '../../model/utility';
import { Subject, takeUntil } from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';
import { DateRangeForm } from '../../custom-date-dialog/custom-date-dialog.component';

@Component({
  selector: 'app-filtered-search-option',
  templateUrl: './filtered-search-option.component.html',
  styleUrl: './filtered-search-option.component.scss',
  standalone: false
})
export class FilteredSearchOptionComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  @Input() singleFilter: FilterField;
  @Input() form: FormGroup<FilterForm>;
  @Input() availableFilters: FilterField[];
  @Input() mode: 'update' | 'create';

  @Output() filter = new EventEmitter<Partial<Filter>>();
  @Output() closed = new EventEmitter<boolean>();

  get isDisabled(): boolean {
    return (
      (typeof this.form.value.value !== 'boolean' && !this.form.value?.value && this.form.value?.value !== 0) ||
      this.form.invalid ||
      !this.selectedFilter
    );
  }

  get selectedFilter(): FilterField { return this.singleFilter || this.availableFilters?.find((f) => f.field === this.form.getRawValue().field); }
  get valueForm(): FormControl<FilterValueType> { return this.form.get('value') as FormControl<FilterValueType>; }
  get dateRangeForm(): FormGroup<DateRangeForm> { return this.form.get('value') as FormGroup<DateRangeForm>; }
  get variables(): FilterVariable[] { return this.selectedFilter?.variables ?? []; }
  get unusedVariables(): FilterVariable[] {
    const value = this.form.value.value;
    if (Array.isArray(value)) {
      return this.variables.filter((v) => !(value as string[]).some((v2) => v2.includes(v.value)));
    }

    if (typeof value === 'string') {
      return this.variables.filter((v) => !value.includes(v.value));
    }

    return this.variables;
  }

  private previous: Partial<Filter>;
  private readonly destroy$ = new Subject<void>();

  constructor(@Inject(L10N_LOCALE) public locale: L10nLocale) {}

  ngOnInit(): void {
    if (this.singleFilter) {
      this.form.patchValue({ field: this.singleFilter.field });
      this.onSelectChange(this.singleFilter.field);
    }

    this.previous = this.form.getRawValue();
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.form.markAsDirty());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addVariable(event: Event, variable: FilterVariable): void {
    event.stopPropagation();
    this.menuTrigger.closeMenu();

    const value = this.form.value.value;
    if (Array.isArray(value)) {
      this.form.patchValue({ value: [...(value ?? []), variable.value] as unknown });
    } else if (typeof value === 'string') {
      if (value.length) {
        this.form.patchValue({ value: `${value}||${variable.value}` });
      } else {
        this.form.patchValue({ value: variable.value });
      }
    } else {
      this.form.patchValue({ value: variable.value });
    }
  }

  onTenantSelect(tenantId: string) {
    if (!isInteger(tenantId)) {
      return;
    }

    this.form.patchValue({ value: tenantId });
  }

  onSelectChange(field: unknown): void {
    const filter = this.singleFilter || this.availableFilters.find((f) => f.field === field);

    const valueControl = this.form.get('value');
    switch (filter?.type) {
      case 'daterange':
      case 'daterange-menu':
        if (valueControl instanceof FormControl) {
          this.form.setControl(
            'value',
            new FormGroup({
              start: new FormControl(undefined),
              end: new FormControl(undefined),
            }),
          );
        }
        break;
      case 'text':
      default:
        this.form.setControl('value', new FormControl(undefined));
        break;
    }
  }

  onSubmit(filter: Partial<Filter>) {
    if (this.isDisabled) {
      return;
    }

    this.form.markAsPristine();
    this.closed.emit(true);
    this.filter.emit({ field: filter.field, value: filter.value, previous: this.previous });
  }
}
